import {
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    TableCell,
    TableRow,
    TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoHint from './InfoHint';
import RoleSelectField from './RoleSelectField';
import SalaryDataTable from './SalaryDataTable';
import SalaryFormSection from './SalaryFormSection';
import useRequest from '../../hooks/useRequest';
import type { SalaryRole, SalaryShift } from '../../domain/salary/types';

interface Props {
    shifts: SalaryShift[];
    roles: SalaryRole[];
    reload: () => Promise<void>;
}

const ShiftsTab = ({ shifts, roles, reload }: Props) => {
    const { t } = useTranslation();
    const request = useRequest();
    const [form, setForm] = useState({
        roleId: 0,
        name: '',
        startTime: '08:00',
        endTime: '16:00',
        isNight: false,
        isWeekendVariant: false,
    });

    const handleCreate = async () => {
        if (!form.roleId || !form.name) return;
        await request({
            path: '/salary/shifts',
            method: 'POST',
            params: form,
        });
        setForm({ ...form, name: '' });
        await reload();
    };

    const handleDelete = async (id: number) => {
        await request({ path: `/salary/shifts/${id}`, method: 'DELETE' });
        await reload();
    };

    const roleName = (roleId: number) => roles.find(r => r.id === roleId)?.name ?? '-';

    return (
        <Stack spacing={2}>
            <SalaryFormSection
                title={
                    <>
                        {t('salary.shifts.addTitle')}
                        <InfoHint title={t('salary.info.shifts.section')} />
                    </>
                }
                onAdd={handleCreate}
                addLabel={t('common.add')}
            >
                <RoleSelectField
                    value={form.roleId}
                    onChange={roleId => setForm({ ...form, roleId })}
                    roles={roles}
                />
                <TextField
                    size="small"
                    label={t('salary.shifts.name')}
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                />
                <TextField
                    size="small"
                    label={t('salary.shifts.startTime')}
                    value={form.startTime}
                    onChange={e => setForm({ ...form, startTime: e.target.value })}
                />
                <TextField
                    size="small"
                    label={t('salary.shifts.endTime')}
                    value={form.endTime}
                    onChange={e => setForm({ ...form, endTime: e.target.value })}
                />
                <FormControlLabel
                    label={t('salary.shifts.isNight')}
                    control={
                        <Switch
                            checked={form.isNight}
                            onChange={e => setForm({ ...form, isNight: e.target.checked })}
                        />
                    }
                />
                <InfoHint title={t('salary.info.shifts.isNight')} />
                <FormControlLabel
                    label={t('salary.shifts.isWeekend')}
                    control={
                        <Switch
                            checked={form.isWeekendVariant}
                            onChange={e => setForm({ ...form, isWeekendVariant: e.target.checked })}
                        />
                    }
                />
                <InfoHint title={t('salary.info.shifts.isWeekend')} />
            </SalaryFormSection>

            <SalaryDataTable
                headers={
                    <>
                        <TableCell>{t('salary.employees.role')}</TableCell>
                        <TableCell>{t('salary.shifts.name')}</TableCell>
                        <TableCell>{t('salary.shifts.startTime')}</TableCell>
                        <TableCell>{t('salary.shifts.endTime')}</TableCell>
                        <TableCell>{t('salary.shifts.isNight')}</TableCell>
                        <TableCell>{t('salary.shifts.isWeekend')}</TableCell>
                    </>
                }
            >
                {shifts.map(shift => (
                    <TableRow key={shift.id}>
                        <TableCell>{roleName(shift.roleId)}</TableCell>
                        <TableCell>{shift.name}</TableCell>
                        <TableCell>{shift.startTime}</TableCell>
                        <TableCell>{shift.endTime}</TableCell>
                        <TableCell>{shift.isNight ? '\u2713' : ''}</TableCell>
                        <TableCell>{shift.isWeekendVariant ? '\u2713' : ''}</TableCell>
                        <TableCell align="right">
                            <IconButton size="small" onClick={() => handleDelete(shift.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </TableCell>
                    </TableRow>
                ))}
            </SalaryDataTable>
        </Stack>
    );
};

export default ShiftsTab;
